import {
  Box,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  useTheme,

} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'

import { useVerificationGuard } from '../../../hooks/auth/useVerificationGuard'
import { useSnackbar } from '../../../hooks/snackbar/useSnackbar'
import { WaterTestStripSelector } from '../../../store/waterTestStrip'
import { WaterTestStripActions } from '../../../store/waterTestStrip/waterTestStrip.slice'
import { BottomActions, ManualUploadHeader, TestParametersContainer } from './components'
import { AppDispatch } from '../../../store'
import { WaterTestParamsActions, WaterTestParamsSelectors } from '../../../store/WaterTestParams'

export const UploadManual = () => {
  const theme = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const { showError } = useSnackbar()
  const isVerified = useVerificationGuard()
  const [selectedValues, setSelectedValues] = useState<Record<string, number>>({})
  const loading = useSelector(WaterTestStripSelector.loading)
  const paramsLoading = useSelector(WaterTestParamsSelectors.isLoading)
  const status = useSelector(WaterTestStripSelector.selectWaterTestStripStatus)

  const waterTestParameters = useSelector(WaterTestParamsSelectors.makeWaterTestParamsValues)

  const loadParameters = useCallback( async () => {
    await dispatch(WaterTestParamsActions.fetchWaterTestParamsAsync())
  },[dispatch])


  const handleValueSelect = (parameterName: string, value: number) => {
    setSelectedValues((prev) => ({
      ...prev,
      [parameterName]: value,
    }))
  }

  const handleBack = () => {
    navigate('/test-strip/upload')
  }

  const handleAnalyze = async () => {
    const selections = waterTestParameters.map((param) => ({
      name: param.name,
      value: selectedValues[param.name],
      unit: param.measurement || '',
    }))

    try {
      const response = await dispatch(
        WaterTestStripActions.manualAnalyzeTestStripAsync(selections) as any,
      )
      if (status === 'failed') {
        showError('Failed to analyze water test strip')
        return
      }

      if (response.payload.status === 'completed') {
        navigate('/test-strip/results')
      } else {
        showError('Something went wrong')
      }
    } catch (error: any) {
      showError(error || 'Failed to analyze water test strip')
    }
  }

  const completedCount = Object.keys(selectedValues).length
  const isAnalyzeDisabled = completedCount < waterTestParameters.length

  useEffect(() => {
    loadParameters()
  },[loadParameters])

  if (!isVerified) {
    return null
  }

  if (loading || paramsLoading) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: 'calc(100vh - 68px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="md">
          <Stack alignItems="center" spacing={2}>
            <Stack alignItems="center">
              <Typography
                fontSize={{ xs: '2rem', md: '2.2rem' }}
                fontWeight={theme.typography.fontWeightBold}
                color="text.primary"
                textAlign={'center'}
              >
                Analyzing your sample
              </Typography>
              <Typography fontSize={'1rem'} color={theme.palette.grey[600]}>
                This might take few seconds
              </Typography>
            </Stack>
            <CircularProgress size={'7rem'} sx={{ color: theme.palette.primary.main }} />
          </Stack>
        </Container>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        minHeight: 'calc(100vh - 68px)',
        py: { xs: 2, md: 3 },
        pb: { xs: 16, md: 20 },
        position: 'relative',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            maxWidth: 800,
            mx: 'auto',
            width: '100%',
            position: 'relative',
          }}
        >
          <Stack spacing={{ xs: 3, md: 4 }}>
            <Box>
              <Typography
                fontSize={{ xs: "32px", md: "40px" }}
                fontWeight={700}
                color="text.primary"
                textAlign="center"
              >
                Manual Upload
              </Typography>

              <Typography
                fontSize={{ xs: "14px", md: "16px" }}
                fontWeight={500}
                color="#475569"
                textAlign="center"
                maxWidth="600px"
                sx={{ mt: { xs: 2, md: 3 }, mx: 'auto' }}
              >
                Match the color on the strip to the closest results for each attributes.
                When done, submit for analysis
              </Typography>
            </Box>

            <Box 
              sx={{ 
                position: 'sticky', 
                top: 0, 
                backgroundColor: 'background.default', 
                zIndex: 1, 
                py: { xs: 1.5, md: 2 },
                mx: { xs: -2, md: 0 },
                px: { xs: 2, md: 0 },
              }}
            >
              <ManualUploadHeader selectedValues={selectedValues} parameters={waterTestParameters} />
            </Box>

            <Stack 
              spacing={3} 
              sx={{ 
                pl: { xs: 0, md: "100px" },
                mt: { xs: 2, md: 3 },
              }}
            >
              <Box sx={{ pl: { xs: 0, md: 1 }, pr: { xs: 0, md: 2 } }}>
                <Divider />
              </Box>
              <TestParametersContainer
                parameters={waterTestParameters}
                selectedValues={selectedValues}
                onValueSelect={handleValueSelect}
              />
            </Stack>
          </Stack>
        </Box>
      </Container>
      <BottomActions
        currentStep={completedCount}
        totalSteps={waterTestParameters.length}
        onBack={handleBack}
        onAnalyze={handleAnalyze}
        isAnalyzeDisabled={isAnalyzeDisabled}
      />
    </Box>
  )
}
